import { createContext, useContext, useReducer } from "react";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { cookieOptions } from "../utils/cookieConfig";
import { getInitialAuthState } from "../utils/authSession";

const cookiesRead = new Cookies();
const { jwtToken, user } = getInitialAuthState(cookiesRead);

// Best-effort: restore Sentry user identity from cookie on page reload.
// The JWT is not re-verified here — the backend validates it on every API call.
// If the session is stale, Sentry.setUser() will be corrected (or cleared) on
// the first authenticated API response. Do not treat this as authoritative identity.
if (jwtToken && user?.id) {
    Sentry.setUser({
        id: String(user.id),
        email: user.email || undefined,
    });
}

export const userInitial = {
    user: user,
    jwtToken: jwtToken,
};

export const AuthContext = createContext(null);
export const AuthDispatchContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, dispatch] = useReducer(userReducer, userInitial);

    return (
        <AuthContext.Provider value={user}>
            <AuthDispatchContext.Provider value={dispatch}>
                {children}
            </AuthDispatchContext.Provider>
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

export function useAuthDispatch() {
    return useContext(AuthDispatchContext);
}

function userReducer(state = userInitial, action) {
    switch (action.type) {
        case "login":
            // Only set R-cookies (standardized across the app) with security flags
            cookiesRead.set("userRId", action.payload.userId, cookieOptions);
            cookiesRead.set("tokenR", action.payload.token, cookieOptions);
            cookiesRead.set("userR", action.payload.user, cookieOptions);

            // Set Sentry user context with email for debugging
            Sentry.setUser({
                id: String(action.payload.userId),
                email: action.payload.user?.email || undefined,
            });

            return {
                ...state,
                user: action.payload.user,
                jwtToken: action.payload.token,
            };
        case "logout":
            // Remove R-cookies and impersonator
            cookiesRead.remove("userRId", cookieOptions);
            cookiesRead.remove("tokenR", cookieOptions);
            cookiesRead.remove("userR", cookieOptions);
            cookiesRead.remove("tokenImpersonator", cookieOptions);

            // Clear Sentry user context
            Sentry.setUser(null);

            // Clear localStorage as well
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.clear();
            }

            // Also clear sessionStorage just in case
            if (typeof window !== 'undefined' && window.sessionStorage) {
                window.sessionStorage.clear();
            }

            return {
                ...state,
                user: {},
                jwtToken: null,
            };
        default:
            return state;
    }
}
